import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { RefreshCw } from "lucide-react";
import { toast } from "sonner";

const COLORS = {
  psora: "#A8896C", sycosis: "#7B906F", syphilis: "#C15C3D",
  tubercular: "#E5C158", cancerous: "#8B5E7A",
};

export default function MiasmPanel({ profileId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await api.miasm(profileId)); }
    catch (e) {
      console.error("miasm failed:", e);
      toast.error(e?.response?.data?.detail || "Failed to load miasm");
    }
    setLoading(false);
  }, [profileId]);
  const refresh = async () => {
    setRefreshing(true);
    try { setData(await api.refreshMiasm(profileId)); toast.success("Refreshed"); }
    catch (err) { console.error("refresh miasm failed:", err); toast.error("Refresh failed"); }
    setRefreshing(false);
  };
  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="text-[#A19E95] font-mono text-sm">Analyzing miasmatic disposition…</div>;
  if (!data) return null;

  const grades = data.grades || {};
  return (
    <div className="space-y-6" data-testid="miasm-panel">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#D4AF37] font-mono">Miasmatic Influence</div>
          <div className="text-[10px] text-[#6E6B64] font-mono mt-1">Derived from planetary afflictions + totality</div>
        </div>
        <Button data-testid="refresh-miasm-btn" onClick={refresh} disabled={refreshing}
                variant="ghost" className="text-[#A19E95] hover:text-[#D4AF37] hover:bg-transparent">
          <RefreshCw size={14} className={refreshing ? "animate-spin mr-2" : "mr-2"} /> Refresh
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="border border-[#2A2D35] bg-[#181A1F] p-5">
          <div className="text-[10px] uppercase tracking-widest text-[#A8896C]">Dominant Miasm</div>
          <div className="font-heading text-3xl mt-1" data-testid="dominant-miasm"
               style={{ color: COLORS[data.dominant_miasm?.toLowerCase()] || "#D4AF37" }}>
            {data.dominant_miasm}
          </div>
          {data.secondary_miasm && (
            <div className="mt-3 text-sm text-[#A19E95]">
              Secondary: <span className="text-[#E6E1D6]">{data.secondary_miasm}</span>
            </div>
          )}
        </div>
        <div className="border border-[#2A2D35] bg-[#181A1F] p-5">
          <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] mb-3">Grade Distribution</div>
          <div className="space-y-2">
            {Object.entries(grades).map(([miasm, val]) => (
              <div key={miasm} className="flex items-center gap-3" data-testid={`miasm-grade-${miasm}`}>
                <span className="w-24 text-xs capitalize text-[#E6E1D6]">{miasm}</span>
                <div className="flex-1 h-2 bg-[#0F1115] rounded-sm overflow-hidden">
                  <div className="h-full transition-all duration-500"
                       style={{ width: `${val}%`, backgroundColor: COLORS[miasm] || "#D4AF37" }} />
                </div>
                <span className="w-10 text-right font-mono text-xs text-[#A19E95]">{val}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="border border-[#2A2D35] bg-[#181A1F] p-5">
        <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] mb-2">Chart-Based Reasoning</div>
        <p className="text-sm text-[#E6E1D6] font-light leading-relaxed">{data.reasoning}</p>
      </div>

      {data.clinical_expressions?.length > 0 && (
        <div className="border border-[#2A2D35] bg-[#181A1F] p-5">
          <div className="text-[10px] uppercase tracking-widest text-[#C15C3D] mb-2">Likely Clinical Expressions</div>
          <ul className="text-sm space-y-1">
            {data.clinical_expressions.map((c, i) => (
              <li key={`ce-${i}-${c.slice(0,20)}`} className="text-[#E6E1D6] pl-3 border-l-2 border-[#2A2D35]">{c}</li>
            ))}
          </ul>
        </div>
      )}

      {data.anti_miasmatic_remedies?.length > 0 && (
        <div className="border border-[#2A2D35] bg-[#181A1F] p-5">
          <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] mb-3">Anti-Miasmatic Remedies</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {data.anti_miasmatic_remedies.map((r) => (
              <div key={r.name} className="border border-[#2A2D35] p-3 bg-[#0F1115]" data-testid={`amr-${r.name}`}>
                <div className="font-heading text-base text-[#E6E1D6]">{r.name}</div>
                <div className="text-[10px] uppercase tracking-widest font-mono mt-1"
                     style={{ color: COLORS[r.miasm?.toLowerCase()] || "#A8896C" }}>{r.miasm}</div>
                <div className="text-xs text-[#A19E95] font-light mt-1">{r.why}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
